import type { WebsocketAwarenessState } from '../../hooks/useWebsocket/websocket';
import type { YjsConfig } from './updateField';

import { use, useEffect, useMemo, useRef } from 'react';
import * as Y from 'yjs';

import WebsocketContext from '../../contexts/WebsocketContext';

import { useFieldContext } from '../form';
import { getYjsInstance, setInYjsParent } from './syncYjs';
import {
  deleteYjsArrayItem,
  insertToYjsArray,
  moveYjsArray,
  replaceAllArray,
  replaceYjsArrayItem,
  swapYjsArray,
  updateYjsText,
} from './updateField';

const INACTIVITY_BLUR_TIMEOUT_MS = 2 * 60 * 1000;

export type UseYjsChangesHookResultBase = {
  update: (newValue: any) => void;
  getYjsInstance: () => any;
  onFocus: () => void;
  onBlur: () => void;
  isBeingEdited: boolean;
  fieldEditors: WebsocketAwarenessState[];
};

type UseYjsChangesHookResult<T extends 'string' | 'array' | 'raw'> =
  T extends 'array'
    ? UseYjsChangesHookResultBase & {
        move: (sourceIndex: number, targetIndex: number) => void;
        swap: (index1: number, index2: number) => void;
        delete: (index: number) => void;
        insert: (value: any, index?: number) => void;
        replace: (index: number, value: any) => void;
      }
    : UseYjsChangesHookResultBase;

const getYjsConfig = (yjsValsMap: Y.Map<any>, fieldName: string) => {
  const parts = fieldName
    .split(/[[.\]]/)
    .filter(Boolean)
    .map((segment) => (/^\d+$/.test(segment) ? +segment : segment));

  const lastSegment = parts.pop();

  let parent: Y.Map<any> | Y.Array<any> = yjsValsMap;
  let parentType: 'map' | 'array' = 'map';

  for (const element of parts) {
    const part = element;
    if (parentType === 'map') {
      parent = (parent as Y.Map<any>).get(part as string);
    } else {
      parent = (parent as Y.Array<any>).get(part as number);
    }
    parentType = parent instanceof Y.Array ? 'array' : 'map';
  }

  return { parent, key: lastSegment!, parentType } as YjsConfig;
};

const useYjsField = <T extends 'string' | 'array' | 'raw'>(yjsType: T) => {
  const inactivityBlurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const field = useFieldContext();
  const { yjsDocument, awareness, editors } = use(WebsocketContext);

  const yjsConfig = useMemo(
    // without useMemo, this changes on every render
    () =>
      yjsDocument
        ? getYjsConfig(yjsDocument.getMap('vals'), field.name)
        : undefined,
    [yjsDocument, field.name],
  );

  const isCollaborating = !!yjsDocument && !!awareness && !!yjsConfig;

  const clearInactivityBlurTimeout = () => {
    if (inactivityBlurTimeoutRef.current) {
      clearTimeout(inactivityBlurTimeoutRef.current);
      inactivityBlurTimeoutRef.current = null;
    }
  };

  const resetInactivityBlurTimeout = () => {
    if (!isCollaborating) return;

    clearInactivityBlurTimeout();
    inactivityBlurTimeoutRef.current = setTimeout(() => {
      awareness.setLocalStateField('activeField', undefined);
      inactivityBlurTimeoutRef.current = null;
    }, INACTIVITY_BLUR_TIMEOUT_MS);
  };

  useEffect(() => clearInactivityBlurTimeout, []);

  const fieldEditors = (editors || []).filter(
    ({ activeField }) =>
      activeField === field.name ||
      activeField?.startsWith(`${field.name}.`) ||
      activeField?.startsWith(`${field.name}[`),
  );

  const isBeingEdited = !!fieldEditors && fieldEditors.length > 0;

  const onFocus = () => {
    if (!isCollaborating) return;

    const currentActiveField = awareness.getLocalState()?.activeField;
    if (currentActiveField !== field.name)
      awareness.setLocalStateField('activeField', field.name);
    resetInactivityBlurTimeout();
  };

  const onBlur = () => {
    if (!isCollaborating) return;
    clearInactivityBlurTimeout();
    awareness.setLocalStateField('activeField', undefined);
  };

  const update = (newValue: any) => {
    if (!isCollaborating) return;

    onFocus();
    resetInactivityBlurTimeout();

    if (yjsType === 'string') {
      return updateYjsText(yjsConfig, newValue);
    } else if (yjsType === 'array') {
      return replaceAllArray(yjsConfig, yjsDocument, newValue);
    }
    return yjsDocument.transact(() => {
      setInYjsParent(yjsConfig.parent, yjsConfig.key, newValue);
    });
  };

  const swap = (index1: number, index2: number) => {
    if (yjsType === 'array' && isCollaborating) {
      swapYjsArray(yjsConfig, yjsDocument, index1, index2);
    }
  };

  const move = (sourceIndex: number, targetIndex: number) => {
    if (yjsType === 'array' && isCollaborating) {
      moveYjsArray(yjsConfig, yjsDocument, sourceIndex, targetIndex);
    }
  };

  const deleteItem = (index: number) => {
    if (yjsType === 'array' && isCollaborating) {
      deleteYjsArrayItem(yjsConfig, index);
    }
  };

  const insert = (value: any, index?: number) => {
    if (yjsType === 'array' && isCollaborating) {
      insertToYjsArray(yjsConfig, value, index);
    }
  };

  const replace = (index: number, value: any) => {
    if (yjsType === 'array' && isCollaborating) {
      replaceYjsArrayItem(yjsConfig, yjsDocument, index, value);
    }
  };

  const getYjsFieldInstance = () => {
    if (!isCollaborating) return;
    return getYjsInstance(yjsConfig.parent, yjsConfig.key);
  };

  return (
    yjsType === 'array'
      ? {
          update,
          move,
          swap,
          delete: deleteItem,
          insert,
          replace,
          getYjsInstance: getYjsFieldInstance,
          onFocus,
          onBlur,
          fieldEditors,
          isBeingEdited,
        }
      : {
          update,
          getYjsInstance: getYjsFieldInstance,
          onFocus,
          onBlur,
          fieldEditors,
          isBeingEdited,
        }
  ) as UseYjsChangesHookResult<T>;
};

export default useYjsField;
